import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, Check, FileText, Image, Play, RotateCcw, Send, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { detectDevice, needsModel, type DetectedDevice } from './detect-device';
import { submitDeviceTest } from './submit';
import {
  DEVICE_TEST_RETENTION_DAYS,
  runTest,
  stepNames,
  type StepName,
  type StepResult,
  type TestKind,
} from './tests';

const TESTS: readonly { readonly kind: TestKind; readonly icon: LucideIcon }[] = [
  { kind: 'image-engine', icon: Image },
  { kind: 'pdf-engine', icon: FileText },
];

type StepView = 'wait' | 'running' | StepResult;

type Run = {
  readonly kind: TestKind;
  readonly steps: readonly StepView[];
  readonly done: boolean;
  readonly send: 'idle' | 'sending' | 'sent' | 'failed';
};

const OTHER = 'other';

/**
 * «Test del dispositivo» (decision 102, PR-10), as on uiux's board (#499, approved by the Director):
 * what the browser knows of the device, one button per test, then the result in place of the list,
 * each step with its time, and «Invia al team», which is never automatic.
 */
export function DeviceTestPage() {
  const { t } = useTranslation('tools');
  const [device, setDevice] = useState<DetectedDevice | null>(null);
  const [model, setModel] = useState('');
  const [run, setRun] = useState<Run | null>(null);
  const active = useActiveContextOptional()?.active ?? null;

  useEffect(() => {
    let current = true;
    void detectDevice().then((detected) => {
      if (current) setDevice(detected);
    });
    return () => {
      current = false;
    };
  }, []);

  const start = async (kind: TestKind): Promise<void> => {
    setRun({ kind, steps: stepNames(kind).map(() => 'wait'), done: false, send: 'idle' });
    await runTest(kind, (index, result) =>
      setRun((previous) =>
        previous === null
          ? previous
          : { ...previous, steps: previous.steps.map((s, i) => (i === index ? result : s)) },
      ),
    );
    setRun((previous) => (previous === null ? previous : { ...previous, done: true }));
  };

  const send = async (): Promise<void> => {
    if (run === null || device === null || active === null) return;
    const results = run.steps.filter((step): step is StepResult => typeof step === 'object');
    setRun({ ...run, send: 'sending' });
    const sent = await submitDeviceTest(active, run.kind, device, model || undefined, results);
    setRun((previous) =>
      previous === null ? previous : { ...previous, send: sent ? 'sent' : 'failed' },
    );
  };

  return (
    <div className="mx-auto max-w-[640px] space-y-6">
      <div>
        <Link
          to="/tools"
          className="mb-2 inline-flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {t('deviceTest.back')}
        </Link>
        <h2 className="screen-title">{t('deviceTest.title')}</h2>
        <p className="screen-summary">{t('deviceTest.summary')}</p>
      </div>

      {run === null ? (
        <>
          <DevicePanel device={device} model={model} onModel={setModel} />
          <TestList disabled={device === null} onStart={(kind) => void start(kind)} />
        </>
      ) : (
        <RunPanel
          run={run}
          canSend={active !== null}
          onSend={() => void send()}
          onRepeat={() => void start(run.kind)}
          onBack={() => setRun(null)}
        />
      )}
    </div>
  );
}

function TestList({ disabled, onStart }: { disabled: boolean; onStart: (kind: TestKind) => void }) {
  const { t } = useTranslation('tools');
  return (
    <ul aria-label={t('deviceTest.tests.label')} className="space-y-2">
      {TESTS.map(({ kind, icon: Icon }) => (
        <li key={kind}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onStart(kind)}
            className="flex min-h-14 w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-accent disabled:opacity-60"
          >
            <span className="flex size-9 items-center justify-center rounded-md bg-selected text-selected-foreground">
              <Icon aria-hidden="true" className="size-5" />
            </span>
            <span className="flex flex-1 flex-col">
              <span className="font-semibold">{t(`deviceTest.tests.${kind}.title`)}</span>
              <span className="text-sm text-muted-foreground">
                {t(`deviceTest.tests.${kind}.duration`)}
              </span>
            </span>
            <Play aria-hidden="true" className="size-4 text-muted-foreground" />
          </button>
        </li>
      ))}
    </ul>
  );
}

type Translate = ReturnType<typeof useTranslation<'tools'>>['t'];

function deviceRows(device: DetectedDevice, t: Translate): [string, string][] {
  const rows: [string, string][] = [
    [t('deviceTest.device.system'), [device.os, device.osVersion].filter(Boolean).join(' ')],
    [
      t('deviceTest.device.browser'),
      [device.browser, device.browserVersion].filter(Boolean).join(' '),
    ],
    [
      t('deviceTest.device.screen'),
      t('deviceTest.device.screenValue', {
        width: device.screen.width,
        height: device.screen.height,
        dpr: device.screen.dpr.toLocaleString('it-IT'),
      }),
    ],
  ];
  if (device.model) rows.push([t('deviceTest.device.model'), device.model]);
  if (device.cores !== undefined) {
    rows.push([
      t('deviceTest.device.memory'),
      device.memoryGb !== undefined
        ? t('deviceTest.device.memoryValue', { gb: device.memoryGb, cores: device.cores })
        : t('deviceTest.device.coresValue', { cores: device.cores }),
    ]);
  }
  return rows;
}

function ModelPicker({
  device,
  model,
  onModel,
}: {
  device: DetectedDevice;
  model: string;
  onModel: (model: string) => void;
}) {
  const { t } = useTranslation('tools');
  const uid = useId();
  const [choice, setChoice] = useState('');
  return (
    <div className="space-y-2">
      <label htmlFor={`${uid}-model`} className="block text-sm font-medium">
        {t('deviceTest.device.askModel')}{' '}
        <span className="font-normal text-muted-foreground">
          {t('deviceTest.device.askModelHint')}
        </span>
      </label>
      <select
        id={`${uid}-model`}
        value={choice}
        onChange={(event) => {
          setChoice(event.target.value);
          onModel(event.target.value === OTHER ? '' : event.target.value);
        }}
        className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
      >
        <option value="">{t('deviceTest.device.chooseModel')}</option>
        {modelsFor(device).map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
        <option value={OTHER}>{t('deviceTest.device.otherModel')}</option>
      </select>
      {choice === OTHER && (
        <input
          aria-label={t('deviceTest.device.otherModelLabel')}
          value={model}
          maxLength={30}
          onChange={(event) => onModel(event.target.value)}
          className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
        />
      )}
    </div>
  );
}

function DevicePanel({
  device,
  model,
  onModel,
}: {
  device: DetectedDevice | null;
  model: string;
  onModel: (model: string) => void;
}) {
  const { t } = useTranslation('tools');
  if (device === null) {
    return <p className="text-sm text-muted-foreground">{t('deviceTest.device.detecting')}</p>;
  }
  const rows = deviceRows(device, t);

  return (
    <section
      aria-label={t('deviceTest.device.title')}
      className="space-y-3 rounded-lg border border-border bg-card p-4"
    >
      <h3 className="text-sm font-semibold">{t('deviceTest.device.title')}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {needsModel(device) && <ModelPicker device={device} model={model} onModel={onModel} />}
    </section>
  );
}

/** A few common models, so most people pick rather than type («Un altro modello…» for the rest). */
function modelsFor(device: DetectedDevice): readonly string[] {
  if (device.os === 'iOS') {
    return ['iPhone 17', 'iPhone 16', 'iPhone 15', 'iPhone 14', 'iPhone 13', 'iPhone SE'];
  }
  return [
    'Samsung Galaxy S25',
    'Samsung Galaxy A55',
    'Samsung Galaxy A54',
    'Google Pixel 9',
    'Google Pixel 8',
    'Xiaomi Redmi Note 13',
  ];
}

function RunPanel({
  run,
  canSend,
  onSend,
  onRepeat,
  onBack,
}: {
  run: Run;
  canSend: boolean;
  onSend: () => void;
  onRepeat: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('tools');
  const names = stepNames(run.kind);
  const finished = run.steps.filter((step) => typeof step === 'object').length;

  return (
    <section aria-label={t(`deviceTest.tests.${run.kind}.title`)} className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold">{t(`deviceTest.tests.${run.kind}.title`)}</h3>
        <span className="text-sm text-muted-foreground">
          {t('deviceTest.count', { done: finished, total: names.length })}
        </span>
      </div>
      <ol className="divide-y divide-border rounded-lg border border-border bg-card">
        {run.steps.map((step, index) => (
          <StepRow key={names[index]} name={names[index] ?? 'photo-load'} step={step} />
        ))}
      </ol>

      {run.done && <SendArea run={run} canSend={canSend} onSend={onSend} onRepeat={onRepeat} />}
      {run.done && (
        <button
          type="button"
          onClick={onBack}
          className="text-link min-h-11 text-sm font-semibold underline-offset-4 hover:underline"
        >
          {t('deviceTest.backToTests')}
        </button>
      )}
    </section>
  );
}

function SendArea({
  run,
  canSend,
  onSend,
  onRepeat,
}: {
  run: Run;
  canSend: boolean;
  onSend: () => void;
  onRepeat: () => void;
}) {
  const { t } = useTranslation('tools');
  return (
    <>
      {run.send === 'sent' && (
        <p role="status" className="flex items-center gap-2 text-sm font-medium">
          <Check aria-hidden="true" className="size-4" />
          {t('deviceTest.sent')}
        </p>
      )}
      {run.send === 'failed' && (
        <div role="alert" className="space-y-2">
          <p className="text-sm text-destructive">{t('deviceTest.sendFailed')}</p>
          <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={onSend}>
            <RotateCcw aria-hidden="true" />
            {t('deviceTest.retry')}
          </Button>
        </div>
      )}
      {(run.send === 'idle' || run.send === 'sending') && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="h-11 sm:h-9"
              disabled={!canSend || run.send === 'sending'}
              onClick={onSend}
            >
              <Send aria-hidden="true" />
              {run.send === 'sending' ? t('deviceTest.sending') : t('deviceTest.send')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              disabled={run.send === 'sending'}
              onClick={onRepeat}
            >
              <RotateCcw aria-hidden="true" />
              {t('deviceTest.repeat')}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('deviceTest.keep', { days: DEVICE_TEST_RETENTION_DAYS })}
          </p>
        </div>
      )}
    </>
  );
}

function stepState(step: StepView): 'ok' | 'ko' | 'wait' | 'running' {
  if (typeof step !== 'object') return step;
  return step.ok ? 'ok' : 'ko';
}

function StepRow({ name, step }: { name: StepName; step: StepView }) {
  const { t } = useTranslation('tools');
  const state = stepState(step);
  return (
    <li className="flex min-h-11 items-start gap-3 px-4 py-2.5">
      <span className="mt-0.5 flex size-5 items-center justify-center">
        {state === 'ok' && (
          <span className="flex size-5 items-center justify-center rounded-full bg-success-tint text-success">
            <Check aria-hidden="true" className="size-3.5" />
          </span>
        )}
        {state === 'ko' && (
          <span className="flex size-5 items-center justify-center rounded-full bg-error-tint text-destructive">
            <X aria-hidden="true" className="size-3.5" />
          </span>
        )}
        {state === 'running' && (
          <span
            aria-hidden="true"
            className="size-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
          />
        )}
        {state === 'wait' && (
          <span aria-hidden="true" className="size-1.5 rounded-full bg-muted-foreground" />
        )}
        <span className="sr-only">{t(`deviceTest.status.${state}`)}</span>
      </span>
      <span className="flex flex-1 flex-col text-sm">
        <span className={cn(state === 'wait' && 'text-muted-foreground')}>
          {t(`deviceTest.steps.${name}`)}
        </span>
        {typeof step === 'object' && step.error && (
          <small className="text-destructive">{t(`deviceTest.errors.${step.error}`)}</small>
        )}
      </span>
      <span className="text-sm tabular-nums text-muted-foreground">
        {typeof step === 'object' && step.ok
          ? t('deviceTest.seconds', {
              value: (step.ms / 1000).toLocaleString('it-IT', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }),
            })
          : ''}
      </span>
    </li>
  );
}
